import { useState, type FormEvent } from 'react';
import type {
  CreateOrganizationInput,
  OrganizationStatus,
  OrganizationType,
} from '@dsr/shared-types';
import { useCreateOrganization } from '../../api/queries/clients';
import { toast } from '../Toasts';
import { ApiError } from '../../api/client';
import { Button, Dialog, FormField, Input, Textarea } from '../ui';
import { ORG_STATUSES, ORG_TYPES } from './meta';

// Create-client dialog. Extracted from the old OrganizationsPage so both
// Directory pages can reuse it: Businesses opens it with the default
// (`corporate`) type; Individuals opens it pre-set to `hni`.
export function CreateOrganizationModal({
  defaultType = 'corporate',
  title = 'New client',
  onClose,
}: {
  defaultType?: OrganizationType;
  title?: string;
  onClose: () => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [legalName, setLegalName] = useState('');
  const [type, setType] = useState<OrganizationType>(defaultType);
  const [industry, setIndustry] = useState('');
  const [status, setStatus] = useState<OrganizationStatus>('active');
  const [tags, setTags] = useState('');
  const [notes, setNotes] = useState('');

  const create = useCreateOrganization();

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const input: CreateOrganizationInput = {
      name: name.trim(),
      type,
      status,
    };
    if (legalName.trim()) input.legal_name = legalName.trim();
    if (industry.trim()) input.industry = industry.trim();
    if (notes.trim()) input.notes = notes.trim();
    const parsedTags = tags
      .split(',')
      .map((t) => t.trim())
      .filter((t) => t.length > 0);
    if (parsedTags.length > 0) input.tags = parsedTags;

    try {
      await create.mutateAsync(input);
      toast.success('Client created.');
      onClose();
    } catch (err) {
      toast.danger(err instanceof ApiError ? err.message : 'Failed to create client.');
    }
  };

  return (
    <Dialog
      title={title}
      open
      onClose={onClose}
      busy={create.isPending}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-org-form"
            variant="primary"
            disabled={create.isPending || name.trim().length === 0}
          >
            {create.isPending ? 'Saving…' : 'Create'}
          </Button>
        </>
      }
    >
      <form id="create-org-form" className="form-grid" onSubmit={onSubmit}>
        <FormField label="Name" required>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
        </FormField>
        <FormField label="Legal name">
          <Input value={legalName} onChange={(e) => setLegalName(e.target.value)} />
        </FormField>
        <FormField label="Type">
          <select
            className="form-input"
            value={type}
            onChange={(e) => setType(e.target.value as OrganizationType)}
          >
            {ORG_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Status">
          <select
            className="form-input"
            value={status}
            onChange={(e) => setStatus(e.target.value as OrganizationStatus)}
          >
            {ORG_STATUSES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Industry">
          <Input value={industry} onChange={(e) => setIndustry(e.target.value)} />
        </FormField>
        <FormField label="Tags (comma-separated)">
          <Input
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="vip, retail, 24-7"
          />
        </FormField>
        <FormField label="Notes" full>
          <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </FormField>
      </form>
    </Dialog>
  );
}
